import { computed, observable, observe, runInAction } from "mobx"
import { toast } from "sonner"

import { type OrgContext, withGlobalLoader } from "@/lib/context.ts"
import { branchDisplayName } from "@/lib/displayUtils.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { openLinkInNewWindow } from "@/lib/std.ts"

import type {
  AiAgentTask,
  AiAgentTaskHistoryItem,
  GitHubPrInfo,
  GitHubStatus,
  PR_Project2,
  PrCreationResult
} from "@/lib/client/generated/TypeDefinitions.ts"
import type { Lifetime } from "@/lib/Lifetime.ts"
import type { Queries } from "@/lib/queries.ts"
import type { GitHubVersionInfo } from "@/routes/tasks/TaskViewScene/TaskVersionsModel.ts"
import type { IComputedValue, IObservableValue } from "mobx"

const log = getLogger("GitHubModel")

export class GitHubModel extends Model {

  readonly shortSummary: IObservableValue<string | null> = observable.box(null)
  private readonly summaryFetchedForVersions = observable.box<AiAgentTaskHistoryItem[] | null>(null)
  readonly gitHubStatus = observable.box<GitHubStatus | null>(null)
  readonly gitHubPrs = observable.box<GitHubPrInfo[]>([])
  private gitHubConnectionUrl: string | null = null
  private defaultBranch: string | null = null

  constructor(
    readonly lifetime: Lifetime,
    readonly context: OrgContext,
    readonly queries: Queries,
    readonly taskId: string,
    readonly task: IObservableValue<AiAgentTask | null>,
    readonly isConfigurationTask: IComputedValue<boolean>,
    readonly project: PR_Project2,
    readonly repositoryName: string,
    readonly historyItems: IObservableValue<AiAgentTaskHistoryItem[]>
  ) {
    super("GitHubModel")

    this.lifetime.add(
      observe(this.isConfigurationTask, change => {
        if (this.task.get() != null && !change.newValue && this.gitHubStatus.get() == null) {
          void this.subscribeToGitHubStatus()
          void this.subscribeToGitHubPrStatus()

          // Preload GitHub Connect URL early
          void this.getGitHubConnectUrl()
        }
      }, true), "githubSubscriptions"
    )

    // Let's avoid unnecessary calls for now
    //
    // this.lifetime.add(
    //   observe(this.historyItems, change => {
    //     if (change.newValue.length > 0) {
    //       void this.fetchShortSummary()
    //     }
    //   }), "historyItemsSub"
    // )
  }

  readonly gitHubVersionInfo = (onCreatePr: (() => void)) => computed(() => {
    const gitHubVersionInfo: GitHubVersionInfo = {
      canOpenPr: this.gitHubStatus.get()?.isGitHubRepo == true && this.gitHubStatus.get()?.isConnected == true,
      existingPrs: this.gitHubPrs.get(),
      onCreatePr: onCreatePr,
    }
    return gitHubVersionInfo
  })

  readonly connectToGitHub = async () => {
    if (this.gitHubConnectionUrl != null) return openLinkInNewWindow(this.gitHubConnectionUrl)

    const url = await withGlobalLoader(this.context, this.getGitHubConnectUrl())
    url && openLinkInNewWindow(url)
  }

  readonly getDefaultBranch = async () => {
    if (this.defaultBranch != null) return this.defaultBranch
    const warmupInfo = await withGlobalLoader(this.context, this.queries.getWarmupInfo(this.project.key, this.repositoryName))
    this.defaultBranch = branchDisplayName(warmupInfo.defaultBranch.head)
    return this.defaultBranch
  }

  readonly logoutGitHub = async () => {
    if (this.gitHubConnectionUrl != null) return openLinkInNewWindow(this.gitHubConnectionUrl)

    const url = await withGlobalLoader(this.context, this.getGitHubConnectUrl())
    url && openLinkInNewWindow(url)
  }

  private async getGitHubConnectUrl() {
    if (this.gitHubConnectionUrl != null) {
      return this.gitHubConnectionUrl
    }
    try {
      const url = await this.queries.apiClient.startGitHubOAuthFlow()
      this.gitHubConnectionUrl = url
      return url
    } catch {
      return null
    }
  }

  private async subscribeToGitHubStatus() {
    try {
      const subscription = await withGlobalLoader(this.context, this.queries.getGitHubStatus(this.project.key, this.repositoryName))
      runInAction(() => this.gitHubStatus.set(subscription.initial))

      const channel = subscription.channel
      this.lifetime.add(channel.subscribe(status => {
        runInAction(() => this.gitHubStatus.set(status))
      }), "GitHubModel:subscribeToGitHubStatus")
    } catch (e) {
      log.error("Failed to subscribe to GitHub status:", e)
    }
  }

  private async subscribeToGitHubPrStatus() {
    try {
      const subscription = await withGlobalLoader(this.context, this.queries.subscribeToOpenedPullRequests([this.taskId]))
      const init = subscription.initial[0].second
      runInAction(() => this.gitHubPrs.set(init.sort((a, b) => a.number - b.number)))

      const channel = subscription.channel
      this.lifetime.add(channel.subscribe(status => {
        runInAction(() => this.gitHubPrs.set(status[0].second.sort((a, b) => a.number - b.number)))
      }), "GitHubModel:subscribeToGitHubPrStatus")
    } catch (e) {
      log.error("Failed to subscribe to GitHub PRs status:", e)
    }
  }

  readonly trackCreatePullRequestClick = () => {
    void this.queries.apiClient.reportCreatePullRequestClickedEvent(this.taskId)
  }

  readonly createPr = async (branchName: string, title: string, description: string | null, shortSummary: string | null) => {
    try {
      const prCreationResult = await withGlobalLoader(this.context, this.queries.apiClient.openPrInGitHub(this.taskId, branchName, title, description, shortSummary))

      if (prCreationResult.className === "Success") {
        openLinkInNewWindow((prCreationResult as PrCreationResult.Success).githubUrl)
        runInAction(() => this.shortSummary.set(null))
        toast.message("PR was successfully created")
      } else {
        const message = (prCreationResult as PrCreationResult.Error).message
        toast.error(`Couldn't open a PR: ${message}`)
      }
      return prCreationResult
    } catch (e) {
      const message = `Couldn't open a PR: ${e instanceof Error ? e.message : ""}`
      toast.error(message)
    }
  }

  async fetchShortSummary() {
    const currentVersions = this.historyItems.get()
    if (this.shortSummary.get() != null && currentVersions === this.summaryFetchedForVersions.get()) return

    runInAction(() => this.isLoading.set(true))

    try {
      const summary = await this.queries.getPrShortSummary(this.taskId)
      runInAction(() => {
        this.shortSummary.set(summary ?? "")
        this.summaryFetchedForVersions.set(currentVersions)
      })
    } finally {
      runInAction(() => this.isLoading.set(false))
    }
  }
}